import { useState } from "react";
import { LogOut, Star, Users } from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import { T } from "../lib/theme";
import type { Settings } from "../lib/settings";
import { ConfirmSheet, Sheet, SheetHeader } from "../components/ui";

export function SettingsSheet({
  session,
  settings,
  set,
  consideringCount,
  onClearConsidering,
  onManage,
  onSignOut,
  onClose,
}: {
  session: Session;
  settings: Settings;
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  consideringCount: number;
  onClearConsidering: () => void;
  onManage: () => void;
  onSignOut: () => void;
  onClose: () => void;
}) {
  const email = session.user.email ?? "";
  const initials = initialsFor(email);
  const [confirmClear, setConfirmClear] = useState(false);

  return (
    <>
    <Sheet onClose={onClose}>
      <div style={{ padding: 20 }}>
        <SheetHeader title="Settings" onClose={onClose} />

        {/* Who am I signed in as — the first thing anyone opens Settings for. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: 13,
            background: T.paper,
            borderRadius: 11,
            marginBottom: 20,
          }}
        >
          <div
            aria-hidden
            style={{
              width: 38,
              height: 38,
              borderRadius: 19,
              background: T.navy,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Signed in</div>
            <div
              style={{ fontSize: 12.5, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {email}
            </div>
          </div>
          <button
            onClick={onSignOut}
            style={{ display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12.5, fontWeight: 600, padding: 6 }}
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <Choice
          label="Due to speak again after"
          value={settings.targetMonths}
          onChange={(v) => set("targetMonths", v)}
          options={[
            [3, "3 mo"],
            [6, "6 mo"],
            [9, "9 mo"],
            [12, "12 mo"],
          ]}
          hint="Sets the countdown scale on Up Next. It doesn't change the order — only how full each gauge looks."
        />

        <Choice
          label="Hide anyone who spoke within"
          value={settings.recentMonths}
          onChange={(v) => set("recentMonths", v)}
          options={[
            [3, "3 mo"],
            [6, "6 mo"],
            [12, "1 yr"],
          ]}
          hint="Used by the filter button on Up Next."
        />

        <Choice
          label="Speaker slots on a new meeting"
          value={settings.speakerSlots}
          onChange={(v) => set("speakerSlots", v)}
          options={[
            [2, "2"],
            [3, "3"],
            [4, "4"],
          ]}
        />

        <Choice
          label="Warn if they prayed within"
          value={settings.prayerWarnMonths}
          onChange={(v) => set("prayerWarnMonths", v)}
          options={[
            [1, "1 mo"],
            [2, "2 mo"],
            [3, "3 mo"],
            [6, "6 mo"],
          ]}
          hint="Shown when picking someone to pray."
        />

        <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 20, paddingTop: 16 }}>
          <button
            onClick={onManage}
            style={{ width: "100%", textAlign: "left", padding: "13px 0", fontSize: 14.5, display: "flex", alignItems: "center", gap: 10 }}
          >
            <Users size={18} style={{ color: T.muted }} /> Ward members
          </button>
          <button
            onClick={() => setConfirmClear(true)}
            disabled={consideringCount === 0}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "13px 0",
              fontSize: 14.5,
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: consideringCount ? T.ink : T.faint,
            }}
          >
            <Star size={18} style={{ color: T.muted }} /> Clear considering list
            {consideringCount > 0 && <span style={{ color: T.faint, fontSize: 13 }}>({consideringCount})</span>}
          </button>
        </div>

        <div style={{ paddingTop: 20, marginTop: 6, borderTop: `1px solid ${T.line}` }}>
          <div style={{ textAlign: "center", fontSize: 11.5, color: T.muted, lineHeight: 1.55, padding: "0 4px" }}>
            This app is created by the Hunter 9th Ward. It is not an official app of The Church of Jesus Christ of
            Latter-day Saints.
          </div>
        </div>
      </div>
    </Sheet>

    {confirmClear && (
      <ConfirmSheet
        title="Clear the considering list?"
        body={
          <>
            This removes all {consideringCount} name{consideringCount === 1 ? "" : "s"} for{" "}
            <strong>everyone</strong>, not just on this device — the considering list is shared by the whole
            bishopric. Nobody is deleted and no history changes; you'd just have to star them again.
          </>
        }
        confirmLabel={`Clear ${consideringCount} name${consideringCount === 1 ? "" : "s"}`}
        onConfirm={() => {
          setConfirmClear(false);
          onClearConsidering();
        }}
        onCancel={() => setConfirmClear(false)}
      />
    )}
    </>
  );
}

/** Segmented picker for the four numeric preferences. */
function Choice({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  options: Array<[number, string]>;
  hint?: string;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="eyebrow" style={{ marginBottom: 7 }}>
        {label}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {options.map(([v, l]) => (
          <button
            key={v}
            onClick={() => onChange(v)}
            aria-pressed={value === v}
            style={{
              flex: 1,
              padding: "9px 0",
              borderRadius: 8,
              fontSize: 13.5,
              fontWeight: 500,
              border: `1px solid ${value === v ? T.navy : T.line}`,
              background: value === v ? T.navy : "#fff",
              color: value === v ? "#fff" : T.ink,
            }}
          >
            {l}
          </button>
        ))}
      </div>
      {hint && <div style={{ fontSize: 11.5, color: T.faint, marginTop: 6, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

/** "brother.clerk@gmail.com" -> "BC". Falls back to the first letter. */
function initialsFor(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[.\-_+]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (local[0] ?? "?").toUpperCase();
}
